import * as _errors from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * User-friendly wrapper for GraphQL `errors[]` responses.
 *
 * Keeping the original errors attached lets retry/debug code inspect the server
 * response while callers still receive a display-safe message.
 */
export class GraphqlResultError extends _errors.UserFriendlyError {
  public override readonly name = 'GraphqlResultError'

  constructor(public readonly errors: readonly unknown[]) {
    super(GraphqlResultError.getUserFriendlyErrorFor(errors).message)
  }
}
export namespace GraphqlResultError {
  /**
   * Preserves user-facing messages from GraphQL formatted errors before the
   * generic error normalizer receives them.
   */
  export function getUserFriendlyErrorFor(
    errors: readonly unknown[],
  ): _errors.UserFriendlyError {
    const userFriendlyErrors = errors.map(error =>
      FormattedError.isFormattedError(error)
        ? new _errors.UserFriendlyError(error.message)
        : _errors.makeUserFriendly(error),
    )

    return userFriendlyErrors.length === 1
      ? userFriendlyErrors[0]
      : new _errors.UserFriendlyAggregateError(userFriendlyErrors)
  }

  /**
   * Whether the request failed only because AppSync stopped waiting on its
   * resolver.
   *
   * AppSync answers a request whose resolver takes longer than 30 seconds with
   * this error, but the resolver itself runs on to completion. For an operation
   * that reports its outcome some other way, such as through a subscription,
   * this is not a failure.
   */
  export function isExecutionTimeout(error: unknown): boolean {
    return (
      error instanceof GraphqlResultError &&
      error.errors.length > 0 &&
      error.errors.every(
        entry =>
          ResolverError.isResolverError(entry) &&
          entry.errorType === EXECUTION_TIMEOUT_ERROR_TYPE,
      )
    )
  }

  /** @see {@link isExecutionTimeout} */
  const EXECUTION_TIMEOUT_ERROR_TYPE = 'Lambda:ExecutionTimeoutException'

  interface ResolverError {
    readonly errorType: string
  }
  namespace ResolverError {
    export const isResolverError = TypeChecker.Factory.object<ResolverError>({
      errorType: TypeChecker.isString,
    })
  }

  interface FormattedError {
    readonly message: string
  }
  namespace FormattedError {
    const isWithMessage = TypeChecker.Factory.object<FormattedError>({
      message: TypeChecker.isString,
    })

    export const isFormattedError: TypeChecker<FormattedError> =
      TypeChecker.Factory.custom(
        (error): error is FormattedError =>
          isWithMessage(error) && error.message.length > 0,
        { typeDescription: 'GraphQL formatted error' },
      )
  }
}
